import { MouseEvent, useRef } from 'react';
import { createPortal } from 'react-dom';

import { icon_close_medium, icon_play } from '@gn-audio/icons';
import { JabraIcon, useFallbackTranslation } from '@gn-audio/jss-components';

import { useVideoModal } from 'common/hooks/useVideoModal';
import VideoEmbed from 'composables/Product/VideoEmbed/VideoEmbed';

export type SimpleVideoSpotProps = {
  youtubeVideoID?: string;
  youkuVideoID?: string;
};

const SimpleVideoSpot = ({ youtubeVideoID, youkuVideoID }: SimpleVideoSpotProps) => {
  const { t } = useFallbackTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const { activeVideoId, setActiveVideoId, closeVideoModal, handleClickOutside } = useVideoModal({
    triggerRef,
    closeButtonRef,
    containerRef,
    modalRef,
  });

  const handleOpenVideo = (event: MouseEvent<HTMLButtonElement>) => {
    triggerRef.current = event.currentTarget;
    setActiveVideoId(youtubeVideoID || youkuVideoID || null);
  };

  const hasVideoSrc = youtubeVideoID || youkuVideoID;

  if (!hasVideoSrc) return null;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleOpenVideo}
        aria-label={t('system.OpenPlayer')}
        data-wt-name="PlayButton"
        className="bg-primary/20 group flex h-[120px] w-[89px] items-center justify-center rounded-full transition-colors hover:bg-gray-700/20"
      >
        <div className="flex h-[96px] w-[65px] items-center justify-center rounded-full bg-primary text-on-brand transition-colors hover:bg-gray-700 hover:text-white">
          <JabraIcon viewBox="0 0 24 24" className="size-7" Icon={icon_play} />
        </div>
      </button>

      {activeVideoId &&
        createPortal(
          <div
            ref={modalRef}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
            role="dialog"
            aria-modal="true"
            aria-label={t('system.VideoPlayer')}
          >
            <div onClick={handleClickOutside} className="absolute inset-0" />
            <button
              onFocus={() => {
                const videoEmbed = modalRef.current?.querySelector('iframe');
                videoEmbed?.focus();
              }}
              className="sr-only"
              aria-hidden="true"
            />
            <button
              aria-label={t('system.ClosePlayer')}
              ref={closeButtonRef}
              onClick={closeVideoModal}
              className="absolute right-6 top-6 flex size-12 items-center justify-center rounded-full bg-primary text-on-brand md:right-9 md:top-9 md:h-20 md:w-20"
            >
              <JabraIcon Icon={icon_close_medium} />
            </button>
            <div className="relative w-full max-w-[90%] md:max-w-[75%]" ref={containerRef}>
              <VideoEmbed
                className="aspect-video size-full bg-black"
                youkuId={youkuVideoID || ''}
                youtubeId={youtubeVideoID || ''}
              />
              <button
                onFocus={() => {
                  closeButtonRef.current?.focus();
                }}
                className="sr-only"
                aria-hidden="true"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default SimpleVideoSpot;
